import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Share2,
  Wallet,
  Pencil,
  LogOut,
  Loader2,
  Eye,
  EyeOff,
  Activity,
  Sparkles,
  ChevronRight,
  Zap,
  Crown,
  Settings,
  HelpCircle,
  ShieldCheck,
  Coins,
  ArrowUpRight,
  ArrowDownLeft,
  ExternalLink,
  HeartHandshake,
  Gem,
  ImagePlus,
  Phone,
  Users,
  TrendingUp,
  Palette,
  Check,
  ShieldAlert,
} from "lucide-react";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { LinkChips } from "@/components/common/LinkChips";
import { useAuth } from "@/context/AuthContext";
import { badgeMeta } from "@/lib/badges";
import { BUBBLE_COLORS, getBubbleKey, setBubbleKey } from "@/lib/bubbleColor";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck, TrustBand } from "@/components/common/TrustBadge";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";
import { ShareToXButton } from "@/components/common/ShareToX";
import { connectWallet, isUserRejection } from "@/lib/walletSign";
import { CRYPTO_ENABLED } from "@/config";
import { shortAddr, ACTIVE_NETWORK } from "@/lib/cryptoSend";
import { ScoreBar } from "@/components/common/ScoreBar";
import { ImageCropper } from "@/components/common/ImageCropper";
import { BandBadgeCard } from "@/components/common/BandBadgeCard";
import { SCORE_BANDS, bandForScore } from "@/lib/trustBands";
import { timeAgo, daysUntil } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const SUB_LABELS = [
  ["identity", "Identity"],
  ["behavior", "Behavior"],
  ["contribution", "Contribution"],
  ["standing", "Standing"],
];

// Trust bands & thresholds (see @/lib/trustBands) and point weights from /admin/score-config.
// "How to reach the next trust band" — progress to the next band + the point-earning actions
// the user hasn't completed yet. Weights are current backend values (client-side; see score-config).
function NextBandCard({ user }) {
  const navigate = useNavigate();
  const { data: walletData } = useQuery({ queryKey: ["wallet-mine"], queryFn: api.walletMine, retry: false, staleTime: 60000 });
  const score = user?.score || 0;
  const walletVerified = (walletData?.wallets || []).length > 0;
  const phoneVerified = !!user?.phone_verified;

  const idx = SCORE_BANDS.reduce((acc, b, i) => (score >= b.min ? i : acc), 0);
  const cur = SCORE_BANDS[idx];
  const next = SCORE_BANDS[idx + 1] || null;

  const actions = [
    { key: "phone", label: "Verify your phone number", points: 13, done: phoneVerified, to: "/app/settings?focus=phone", Icon: Phone },
    { key: "wallet", label: "Verify your wallet", points: 3, done: walletVerified, to: "/app/wallet", Icon: Wallet },
    { key: "bloq", label: "Create a BLOQ", points: 2, done: false, evergreen: true, to: "/app/bloqs", Icon: Users },
    { key: "invite", label: "Invite a friend who verifies", points: 2, done: false, evergreen: true, to: "/app/referrals", Icon: Gift },
  ];
  const todo = actions.filter((a) => !a.done);

  if (!next) {
    return (
      <div className="mt-4 rounded-3xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-5" data-testid="next-band-card">
        <div className="flex items-center gap-2 text-sm font-bold text-white">
          <Crown className="h-4 w-4 text-bloq-purple" /> You're in the top band — High Trust 🏆
        </div>
        <p className="mt-1 text-xs text-bloq-muted">You've earned the highest trust tier on BLOQ. Keep it by staying active and scam-free.</p>
      </div>
    );
  }

  const pointsNeeded = Math.max(1, next.min - score);
  const span = next.min - cur.min;
  const pct = Math.max(0, Math.min(100, ((score - cur.min) / span) * 100));

  return (
    <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="next-band-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold text-white">
          <TrendingUp className="h-4 w-4 text-bloq-mint" /> Reach {next.name}
        </div>
        <span className="shrink-0 rounded-full bg-bloq-mint/15 px-2.5 py-0.5 text-xs font-bold text-bloq-mint" data-testid="next-band-points">
          {pointsNeeded} pts to go
        </span>
      </div>
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-[11px] font-semibold text-bloq-muted">
          <span>{cur.name} · {cur.min}</span>
          <span>{next.name} · {next.min}</span>
        </div>
        <ScoreBar value={pct} height="h-2.5" />
      </div>
      <div className="mt-4 space-y-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-bloq-muted">Quick ways to earn points</div>
        {todo.map((a) => (
          <button
            key={a.key}
            onClick={() => navigate(a.to)}
            className="flex w-full items-center gap-3 rounded-xl border border-white/8 bg-bloq-s2 p-2.5 text-left transition-colors hover:bg-white/5"
            data-testid={`next-band-action-${a.key}`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bloq-mint/12 text-bloq-mint">
              <a.Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{a.label}</span>
            <span className="shrink-0 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[11px] font-bold text-bloq-mint">
              +{a.points}{a.evergreen ? " ea" : ""}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-bloq-muted" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Link a receiving wallet so friends can send you crypto in a DM. Persists via
// PUT /users/me/receiving-wallet; verified:true when it matches a signature-proven wallet
// (Wallet proof), else it saves as unverified. LIVE on prod.
// Recent crypto activity (Lane A) — a home for sends & receipts beyond the in-thread receipt.
function CryptoActivityCard() {
  const { data, isLoading } = useQuery({ queryKey: ["crypto-history"], queryFn: () => api.cryptoHistory(), retry: false });
  const items = data?.items || [];
  return (
    <div className="mt-4 rounded-3xl border border-white/10 bg-bloq-s1 p-5" data-testid="crypto-activity-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Coins className="h-4 w-4 text-bloq-mint" /> Recent crypto activity</div>
      {isLoading ? (
        <div className="mt-3 flex h-12 items-center justify-center text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-bloq-muted" data-testid="crypto-activity-empty">No sends or receipts yet. Send crypto from any chat to see it here.</p>
      ) : (
        <div className="mt-3 space-y-2.5">
          {items.slice(0, 6).map((it) => {
            const out = it.direction === "sent" || it.direction === "out";
            return (
              <div key={it.id} className="flex items-center gap-3" data-testid={`crypto-activity-${it.id}`}>
                <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", out ? "bg-white/8 text-bloq-muted" : "bg-bloq-mint/15 text-bloq-mint")}>
                  {out ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownLeft className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{out ? "Sent to" : "From"} {it.peer?.display_name || (it.peer?.handle ? `@${it.peer.handle}` : "someone")}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-bloq-muted">
                    <span className="capitalize">{it.status}</span>
                    {it.explorer_url && <a href={it.explorer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-bloq-mint underline">view <ExternalLink className="h-2.5 w-2.5" /></a>}
                  </div>
                </div>
                <div className={cn("shrink-0 text-sm font-bold", out ? "text-white" : "text-bloq-mint")}>{out ? "−" : "+"}{it.amount} {it.symbol}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BubbleColorCard() {
  const [key, setKey] = useState(getBubbleKey);
  const choose = (k) => {
    const prev = key;
    setKey(k);
    setBubbleKey(k); // instant local re-tint
    // Persist to the profile (syncs to mobile); revert on failure so the pill never lies.
    api.updateProfile({ bubble_color: k })
      .then(() => toast.success("Bubble colour updated"))
      .catch(() => { setKey(prev); setBubbleKey(prev); toast.error("Couldn't save your bubble colour"); });
  };
  return (
    <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="bubble-color-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Palette className="h-4 w-4 text-bloq-mint" /> My bubble colour
      </div>
      <p className="mb-3 text-xs text-bloq-muted">Pick the tint for your own chat bubbles — it re-tints live in every chat on this device.</p>
      <div className="flex flex-wrap gap-2.5" data-testid="bubble-color-swatches">
        {BUBBLE_COLORS.map((c) => (
          <button
            key={c.key}
            onClick={() => choose(c.key)}
            className={cn(
              "relative h-10 w-10 rounded-full ring-2 ring-offset-2 ring-offset-bloq-s1 transition-all",
              key === c.key ? "scale-105 ring-white" : "ring-transparent hover:ring-white/30",
            )}
            style={{ background: c.swatch }}
            data-testid={`bubble-color-${c.key}`}
            title={c.label}
            aria-label={c.label}
          >
            {key === c.key && <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function CryptoWalletCard() {
  const qc = useQueryClient();
  const chain = ACTIVE_NETWORK.key;
  const [busy, setBusy] = useState(false);

  const { data: wallet, isLoading } = useQuery({
    queryKey: ["my-receiving-wallet", chain],
    queryFn: () => api.myReceivingWallet(chain),
    retry: false,
  });

  const saveMut = useMutation({
    mutationFn: (address) => api.setReceivingWallet({ address, chain, family: "evm" }),
    onSuccess: (res) => {
      qc.setQueryData(["my-receiving-wallet", chain], res?.wallet || null);
      toast.success(res?.wallet?.verified ? "Receiving wallet saved · verified" : "Receiving wallet saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Couldn't save your wallet."),
  });

  const clearMut = useMutation({
    mutationFn: () => api.clearReceivingWallet(chain),
    onSuccess: () => { qc.setQueryData(["my-receiving-wallet", chain], null); toast.success("Receiving wallet removed"); },
    onError: () => toast.error("Couldn't remove your wallet."),
  });

  const connect = async (via) => {
    setBusy(true);
    try {
      const w = await connectWallet("evm", via === "wc" ? { via: "walletconnect" } : {});
      await saveMut.mutateAsync(w.address);
    } catch (e) {
      if (isUserRejection(e)) toast.info("Canceled");
      else toast.error(e?.message || "Couldn't connect a wallet.");
    } finally {
      setBusy(false);
    }
  };

  const addr = wallet?.address;
  const verified = !!wallet?.verified;
  const copy = () => { navigator.clipboard?.writeText(addr); toast.success("Address copied"); };

  return (
    <div className="mt-4 rounded-3xl border border-white/10 bg-bloq-s1 p-5" data-testid="crypto-wallet-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><Coins className="h-4 w-4 text-bloq-mint" /> Crypto wallet</div>
      <p className="mt-0.5 text-xs text-bloq-muted">Link a wallet so friends can send you USDC &amp; ETH in chat. <span className="text-amber-300">Base Sepolia · testnet</span>.</p>
      {isLoading ? (
        <div className="mt-3 flex h-11 items-center justify-center text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : addr ? (
        <>
          <div className="mt-3 flex items-center gap-2 rounded-2xl border border-bloq-mint/25 bg-bloq-mint/[0.06] px-3 py-3" data-testid="crypto-wallet-connected">
            <Wallet className="h-4 w-4 shrink-0 text-bloq-mint" />
            <span className="min-w-0 flex-1 truncate font-mono text-sm" title={addr}>{shortAddr(addr)}</span>
            {verified
              ? <span className="shrink-0 rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-mint" data-testid="crypto-wallet-verified">Verified</span>
              : <span className="shrink-0 rounded-full border border-white/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-muted" data-testid="crypto-wallet-unverified">Unverified</span>}
            <button onClick={copy} className="shrink-0 rounded-full border border-white/12 px-3 py-1 text-xs font-semibold text-bloq-mint hover:bg-bloq-mint/10" data-testid="crypto-wallet-copy">Copy</button>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <button onClick={() => connect()} disabled={busy || saveMut.isPending} className="text-xs font-semibold text-bloq-mint hover:underline disabled:opacity-50" data-testid="crypto-wallet-change">{busy ? "Connecting…" : "Change wallet"}</button>
            <button onClick={() => clearMut.mutate()} disabled={clearMut.isPending} className="text-xs font-semibold text-bloq-muted hover:text-destructive disabled:opacity-50" data-testid="crypto-wallet-remove">Remove</button>
          </div>
          {!verified && <p className="mt-2 text-[11px] text-bloq-muted">Prove ownership in <span className="text-bloq-mint">Wallet proof</span> to earn the Verified badge.</p>}
        </>
      ) : (
        <>
          <Button onClick={() => connect()} disabled={busy || saveMut.isPending} className="mt-3 h-11 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="crypto-wallet-connect">
            {busy || saveMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Wallet className="mr-1.5 h-4 w-4" /> Connect wallet</>}
          </Button>
          <button onClick={() => connect("wc")} disabled={busy || saveMut.isPending} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 py-2.5 text-xs font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15 disabled:opacity-50" data-testid="crypto-wallet-connect-wc">
            <Wallet className="h-3.5 w-3.5" /> Use WalletConnect · Qastle
          </button>
        </>
      )}
    </div>
  );
}

const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";

// Shareable "trust passport" — links unfurl with a rich OG card (web-owned renderer:
// GET /api/u/<handle>?s&b&v → /api/og/trust/<handle>.png). Uses the signed-in user's own score.
function TrustCardShare({ user }) {
  const [badgeOpen, setBadgeOpen] = useState(false);
  const band = user.score_band || "";
  const badgeBand = bandForScore(user.score || 0).name;
  const shareUrl = `${SHARE_BASE}/api/u/${encodeURIComponent(user.username)}?s=${user.score || 0}&b=${encodeURIComponent(band)}&v=${user.verified ? 1 : 0}`;
  const tweet = `My BLOQ Score is ${user.score} 🛡️ — quantum-secure, trust-gated messaging for Web3. Every stranger screened, every scam blocked. Verify me 👇`;
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Trust card link copied");
  };
  return (
    <div className="mt-4 overflow-hidden rounded-3xl border border-bloq-mint/20 bg-gradient-to-br from-bloq-mint/[0.07] to-bloq-purple/[0.05] p-5" data-testid="trust-card-share">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <ShieldCheck className="h-4 w-4 text-bloq-mint" /> Share your trust card
      </div>
      <p className="mt-0.5 text-xs text-bloq-muted">Flex your reputation — links unfurl with a rich card on X, Telegram &amp; more.</p>
      <div className="mt-3 flex items-center gap-4 rounded-2xl border border-white/10 bg-bloq-bg/60 p-4" data-testid="trust-card-preview">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-bloq-muted">BLOQ Score</div>
          <div className="font-heading text-4xl font-bold leading-none">{user.score}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 truncate font-semibold">@{user.username}{user.verified && <VerifiedCheck size={14} />}</div>
          <TrustBand band={band} className="mt-1 px-2 py-0.5 text-[10px]" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ShareToXButton text={tweet} url={shareUrl} className="bg-white px-4 py-2 text-sm font-bold text-black hover:opacity-90" testid="trust-card-share-x" />
        <Button onClick={() => setBadgeOpen(true)} className="rounded-full bloq-gradient text-sm font-bold text-bloq-bg hover:opacity-90" data-testid="trust-card-badge-btn">
          <Share2 className="mr-1 h-4 w-4" /> Share my badge
        </Button>
        <Button onClick={copyLink} variant="outline" className="rounded-full border-white/12 bg-transparent text-bloq-mint hover:bg-bloq-mint/10" data-testid="trust-card-copy-btn">
          <Copy className="mr-1 h-4 w-4" /> Copy link
        </Button>
        <a
          href={`/u/${encodeURIComponent(user.username)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-white/12 px-4 py-2 text-sm font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/10"
          data-testid="trust-card-view-public-btn"
        >
          <ShieldCheck className="h-4 w-4" /> View public profile
        </a>
      </div>
      <Dialog open={badgeOpen} onOpenChange={setBadgeOpen}>
        <DialogContent className="max-w-sm border-bloq-mint/30 bg-bloq-s1" data-testid="trust-card-badge-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Your {badgeBand} badge</DialogTitle>
            <DialogDescription className="text-center text-bloq-muted">Post your trust badge anywhere.</DialogDescription>
          </DialogHeader>
          <BandBadgeCard band={badgeBand} score={user.score} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditProfileDialog({ open, onOpenChange, user }) {
  const { refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [avatar, setAvatar] = useState(user?.avatar_b64 || "");
  const [cropSrc, setCropSrc] = useState(null);
  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    username: user?.username || "",
    bio: user?.bio || "",
  });

  useEffect(() => {
    if (open) {
      setAvatar(user?.avatar_b64 || "");
      setCropSrc(null);
      setForm({ display_name: user?.display_name || "", username: user?.username || "", bio: user?.bio || "" });
    }
  }, [open, user]);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result); // open the pan/zoom cropper
    reader.onerror = () => toast.error("Couldn't read that image — try another.");
    reader.readAsDataURL(file);
  };

  const mut = useMutation({
    mutationFn: (payload) => api.updateProfile(payload),
    onSuccess: async () => {
      await refreshUser();
      toast.success("Profile updated");
      onOpenChange(false);
    },
    onError: (e) => {
      const blocked = shieldBlockedMessage(e);
      if (blocked) { setShieldError(blocked); return; }
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" && d) || d?.message || "Could not update profile";
      toast.error(/cooldown|too soon|wait/i.test(msg) ? "You changed your photo recently — please try again later." : msg);
    },
  });
  const [shieldError, setShieldError] = useState(null);

  const save = () => {
    const payload = { ...form };
    if (avatar !== (user?.avatar_b64 || "")) payload.avatar_b64 = avatar; // "" clears, data URI sets; only send when changed
    mut.mutate(payload);
  };

  const avatarUser = { ...user, avatar_b64: avatar, nft_avatar: avatar ? null : user?.nft_avatar };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="edit-profile-dialog">
        {cropSrc ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Adjust your photo</DialogTitle>
              <DialogDescription className="text-bloq-muted">Drag to reposition, slide to zoom.</DialogDescription>
            </DialogHeader>
            <ImageCropper
              src={cropSrc}
              onCancel={() => setCropSrc(null)}
              onCropped={(dataUri) => { setAvatar(dataUri); setCropSrc(null); }}
            />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Edit profile</DialogTitle>
              <DialogDescription className="text-bloq-muted">
                Update how you appear across BLOQ Talk.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="group relative shrink-0 rounded-2xl outline-none"
              data-testid="edit-avatar-btn"
              title="Change profile picture"
            >
              {avatar ? (
                <img src={avatar} alt="Your avatar" className="h-16 w-16 rounded-2xl object-cover" data-testid="edit-avatar-preview" />
              ) : (
                <Avatar user={avatarUser} size="lg" flair={false} />
              )}
              <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <ImagePlus className="h-5 w-5 text-white" />
              </span>
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white">Profile picture</div>
              <div className="mt-0.5 flex items-center gap-3">
                <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-semibold text-bloq-mint hover:underline" data-testid="edit-avatar-upload">
                  {avatar ? "Change photo" : "Upload photo"}
                </button>
                {avatar && (
                  <button type="button" onClick={() => setAvatar("")} className="text-xs font-semibold text-bloq-muted hover:text-destructive" data-testid="edit-avatar-remove">
                    Remove
                  </button>
                )}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="edit-avatar-input" />
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Display name</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="edit-display-name-input"
            />
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Username</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="edit-username-input"
            />
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Bio</Label>
            <Textarea
              value={form.bio}
              onChange={(e) => { setShieldError(null); setForm((f) => ({ ...f, bio: e.target.value })); }}
              className={cn("mt-1 rounded-xl border-white/12 bg-bloq-bg", shieldError && "border-destructive/60")}
              data-testid="edit-bio-input"
            />
            {shieldError && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive" data-testid="edit-bio-shield-error">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {shieldError}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={mut.isPending}
            className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
            data-testid="save-profile-btn"
          >
            {mut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [revealCode, setRevealCode] = useState(false);

  // Deep-link from the activation checklist: /app/profile?edit=1 opens the edit dialog.
  useEffect(() => {
    if (searchParams.get("edit") === "1") {
      setEditOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: invite } = useQuery({ queryKey: ["invite"], queryFn: api.myInvite });
  const { data: history } = useQuery({ queryKey: ["score-history"], queryFn: api.scoreHistory });
  const { data: sub } = useQuery({ queryKey: ["subscription"], queryFn: api.subscriptionStatus });
  const { data: mySubs } = useQuery({ queryKey: ["my-subscriptions"], queryFn: api.mySubscriptions, retry: false });
  const expiringMemberships = (mySubs?.subscriptions || []).filter((s) => { const d = daysUntil(s.expires_at); return d !== null && d <= 5; }).length;
  const plan = sub?.subscription;
  const isPaid = !!plan && plan.plan !== "free" && plan.is_active;

  if (!user) return null;

  const code = invite?.code || "";
  const masked = "•".repeat(Math.max(code.length, 10));

  const copyCode = () => {
    navigator.clipboard?.writeText(code);
    toast.success("Invite code copied");
  };
  const shareCode = async () => {
    const inviteUrl = `${SHARE_BASE}/?invite=${code}`;
    const shareData = {
      title: "Join me on BLOQ Talk",
      text: `Join me on BLOQ Talk with my invite code ${code}`,
      url: inviteUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* cancelled */
      }
    } else {
      navigator.clipboard?.writeText(`${shareData.text} — ${inviteUrl}`);
      toast.success("Invite link copied to clipboard");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg">
      <div className="mx-auto max-w-2xl px-5 py-8 sm:px-8">
        {/* Identity */}
        <div className="flex flex-col items-center text-center">
          {isPaid ? (
            <div className="relative inline-flex items-center justify-center" data-testid="premium-avatar-ring">
              <div className="absolute inset-0 -m-[5px] rounded-[2rem] bg-[conic-gradient(from_0deg,#01FABD,#3591F4,#8B5CF6,#01FABD)] animate-[spin_6s_linear_infinite]" aria-hidden />
              <div className="absolute inset-0 -m-[1px] rounded-[1.85rem] bg-bloq-bg" aria-hidden />
              <Avatar user={user} size="xl" className="relative" flair={false} />
            </div>
          ) : (
            <Avatar user={user} size="xl" flair={false} />
          )}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <h1 className="text-2xl font-bold">{user.display_name}</h1>
            {user.verified && <VerifiedCheck size={20} />}
            <TopConnectorCrown match={user} me />
            {user.nft_avatar && (
              <span className="inline-flex items-center gap-1 rounded-full border border-bloq-purple/40 bg-bloq-purple/15 px-2 py-0.5 text-[10px] font-bold uppercase text-bloq-purple" data-testid="nft-pfp-badge" title={`${user.nft_avatar.name || "NFT"} · ${user.nft_avatar.chain || ""}`}>
                <Gem className="h-3 w-3" /> NFT verified
              </span>
            )}
            {isPaid && (
              <span className="inline-flex items-center gap-1 rounded-full border border-bloq-purple/40 bg-bloq-purple/15 px-2 py-0.5 text-[10px] font-bold uppercase text-bloq-purple" data-testid="premium-badge">
                <Crown className="h-3 w-3" /> {plan.plan_name}
              </span>
            )}
            {(user.badges || []).map((b) => {
              const M = badgeMeta(b);
              const I = M.Icon;
              return (
                <span key={b} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${M.chip}`} data-testid={`profile-badge-${b}`}>
                  <I className="h-3 w-3" /> {M.label}
                </span>
              );
            })}
          </div>
          <div className="text-bloq-muted">@{user.username}</div>
          {user.bio && <p className="mt-2 max-w-md text-sm text-white/75">{user.bio}</p>}
          <LinkChips links={user.links} testid="profile-bio-links" className="mt-2" />
        </div>

        {/* BLOQ Score */}
        <div className="relative mt-7 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-bloq-s2 to-bloq-s1 p-6" data-testid="bloq-score-card">
          <div className="pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full bg-bloq-purple/12 blur-3xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-bloq-muted">BLOQ Score</div>
              <div className="font-heading text-5xl font-bold leading-none">{user.score}</div>
            </div>
            <TrustBand band={user.score_band} className="mt-1 px-3 py-1 text-xs" />
          </div>
          <ScoreBar value={user.score} className="mt-4" height="h-2.5" />
          <p className="mt-3 text-sm text-bloq-muted">Your portable trust passport across BLOQ</p>

          <div className="mt-5 space-y-3">
            {SUB_LABELS.map(([key, label]) => (
              <div key={key} className="flex items-center gap-3" data-testid={`score-sub-${key}`}>
                <span className="w-24 shrink-0 text-sm text-white/75">{label}</span>
                <ScoreBar value={user.score_components?.[key] || 0} variant="blue" />
                <span className="w-8 shrink-0 text-right text-sm font-semibold">
                  {user.score_components?.[key] ?? 0}
                </span>
              </div>
            ))}
          </div>

          {!isPaid && (
            <button
              onClick={() => navigate("/app/membership")}
              className="mt-5 flex w-full items-center justify-between rounded-2xl border border-bloq-mint/25 bg-bloq-mint/[0.06] px-4 py-3 text-left transition-colors hover:bg-bloq-mint/10"
              data-testid="upgrade-nudge"
            >
              <div className="flex items-center gap-2.5">
                <Zap className="h-4 w-4 shrink-0 text-bloq-mint" />
                <div>
                  <div className="text-sm font-semibold text-white">Upgrade to BLOQ+</div>
                  <div className="text-xs text-bloq-muted">Unlimited AI summaries, HD sharing & a premium badge</div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-bloq-mint" />
            </button>
          )}
        </div>

        <NextBandCard user={user} />

        <TrustCardShare user={user} />

        {CRYPTO_ENABLED && <CryptoWalletCard />}
        {CRYPTO_ENABLED && <CryptoActivityCard />}

        <BubbleColorCard />

        {/* Trust activity */}
        <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="trust-activity-card">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Activity className="h-4 w-4 text-bloq-mint" /> Trust activity
          </div>
          {(history?.events || []).length === 0 ? (
            <p className="text-sm text-bloq-muted" data-testid="trust-activity-empty">
              No trust events yet — your score updates as you build reputation on BLOQ.
            </p>
          ) : (
            <div className="space-y-2.5">
              {(history.events || []).slice(0, 8).map((ev, i) => (
                <div key={ev.id || i} className="flex items-center gap-3" data-testid="trust-event-row">
                  <span
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold tabular-nums",
                      (ev.delta ?? 0) >= 0 ? "bg-bloq-mint/15 text-bloq-mint" : "bg-destructive/15 text-destructive",
                    )}
                  >
                    {ev.delta > 0 ? `+${ev.delta}` : (ev.delta ?? "•")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">
                      {ev.reason || (ev.type || "").replace(/_/g, " ") || "Score update"}
                    </div>
                    <div className="text-xs text-bloq-muted">
                      {ev.source ? `${ev.source} · ` : ""}{timeAgo(ev.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          variant="outline"
          onClick={() => setEditOpen(true)}
          className="mt-4 h-12 w-full rounded-full border-2 border-bloq-mint/60 bg-transparent font-bold text-bloq-mint hover:bg-bloq-mint/10"
          data-testid="edit-profile-btn"
        >
          <Pencil className="mr-1 h-4 w-4" /> Edit profile
        </Button>

        {/* Invite friends */}
        <div className="mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="invite-card">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
              <Gift className="h-5 w-5" />
            </span>
            <div>
              <div className="font-semibold">Invite & earn</div>
              <div className="text-xs text-bloq-muted">Give 7 days of BLOQ+ · earn free months</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setRevealCode((r) => !r)}
              className="flex flex-1 items-center justify-between rounded-xl border border-white/10 bg-bloq-bg px-4 py-3 font-mono tracking-widest"
              data-testid="reveal-invite-code"
            >
              <span>{revealCode ? code : masked}</span>
              {revealCode ? <EyeOff className="h-4 w-4 text-bloq-muted" /> : <Eye className="h-4 w-4 text-bloq-muted" />}
            </button>
            <Button onClick={copyCode} variant="outline" className="rounded-xl border-white/12 bg-transparent text-bloq-mint hover:bg-bloq-mint/10" data-testid="copy-invite-btn">
              <Copy className="mr-1 h-4 w-4" /> Copy
            </Button>
            <Button onClick={shareCode} variant="outline" className="rounded-xl border-white/12 bg-transparent text-bloq-mint hover:bg-bloq-mint/10" data-testid="share-invite-btn">
              <Share2 className="mr-1 h-4 w-4" /> Share
            </Button>
          </div>
          {invite?.uses != null && (
            <p className="mt-2 text-xs text-bloq-muted">{invite.uses} invites remaining</p>
          )}
          <button
            onClick={() => navigate("/app/referrals")}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bloq-gradient py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
            data-testid="view-referrals-btn"
          >
            <Gift className="h-4 w-4" /> View rewards & track referrals
          </button>
        </div>

        {/* Membership */}
        <button
          onClick={() => navigate("/app/membership")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-bloq-mint/30"
          data-testid="membership-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Membership</div>
            <div className="text-xs text-bloq-muted" data-testid="membership-current-plan">
              {plan?.plan_name || "Free"} plan{plan?.is_active ? " · active" : ""}
            </div>
          </div>
          <span className="rounded-md border border-bloq-mint/40 bg-bloq-mint/15 px-2 py-0.5 text-[10px] font-bold uppercase text-bloq-mint">
            {plan?.plan && plan.plan !== "free" ? "Manage" : "Upgrade"}
          </span>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        {/* Creator Hub */}
        <button
          onClick={() => navigate("/app/creator")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-bloq-purple/30"
          data-testid="creator-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
            <Crown className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Creator Hub</div>
            <div className="text-xs text-bloq-muted">Monetize your BLOQs · earnings & payouts</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        {/* Memberships (fan side) */}
        <button
          onClick={() => navigate("/creators")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-bloq-purple/30"
          data-testid="memberships-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
            <HeartHandshake className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Memberships</div>
            <div className="text-xs text-bloq-muted">Creators you support · renewals</div>
          </div>
          {expiringMemberships > 0 && (
            <span className="mr-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-400 px-1.5 text-[11px] font-bold text-bloq-bg" data-testid="memberships-expiring-badge">
              {expiringMemberships}
            </span>
          )}
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        {/* Wallet proof */}
        <button
          onClick={() => navigate("/app/wallet")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-bloq-blue/30"
          data-testid="wallet-proof-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-blue/20 text-bloq-blue">
            <Wallet className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Wallet proof</div>
            <div className="text-xs text-bloq-muted">Read-only signature · never your keys</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        <button
          onClick={() => navigate("/app/settings")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-white/20"
          data-testid="settings-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/8 text-white/80">
            <Settings className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Settings</div>
            <div className="text-xs text-bloq-muted">Notifications & account</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        <button
          onClick={() => navigate("/app/help")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-white/20"
          data-testid="help-center-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/8 text-white/80">
            <HelpCircle className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">Help &amp; Getting Started</div>
            <div className="text-xs text-bloq-muted">FAQs, guides & your setup checklist</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        <button
          onClick={() => navigate("/app/spaces")}
          className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-white/8 bg-bloq-s1 p-5 text-left transition-colors hover:border-white/20"
          data-testid="my-spaces-row"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bloq-purple/15 text-bloq-purple">
            <Users className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <div className="font-semibold">My spaces</div>
            <div className="text-xs text-bloq-muted">BLOQs, Circles &amp; Channels you own or joined — leave or delete in one place</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-muted" />
        </button>

        <button
          onClick={handleLogout}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-white/8 py-3 text-sm text-bloq-muted transition-colors hover:text-white"
          data-testid="profile-logout-btn"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} user={user} />
    </div>
  );
}
